/**
 * Data layer for the community threads of `/show/:id` and
 * `/show/:id/episode/:season/:episode` (web/docs/data-layer.md). Mirrors the
 * mobile community client (`app/src/data/api-real.ts` + `community-store.ts` —
 * shapes and behaviour only, nothing imported): reads degrade on `404`, and
 * every write mutation is optimistic — patch in `onMutate`, roll back in
 * `onError`, merge the server ack in `onSuccess`, with a toast + Retry on
 * failure (web has no persisted mutation queue — ui-ux.md §7).
 *
 * A thread is addressed by a structured `CommunityTarget` from `@shared/types`
 * — a title-level partition (`movie`/`show`) or one episode's own partition
 * (`title_type=episode`, its own DynamoDB partition key). Keys, targets and
 * query params carry the season/episode coordinates for episode threads.
 *
 * Comment creation is idempotent via a permanent client mutation ID
 * (`m<base36-ts>-<seq>`, the app's format) which becomes the `comment_id` —
 * the optimistic entry and the server ack share one id, so the ack merges
 * over the optimistic row without duplicates (docs/api-schema.md §Community).
 */

import { useRef } from 'react';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import { apiDelete, apiGet, apiPost, apiPut, ApiError } from '@/api/http';
import { translate } from '@/i18n/locale';
import { getLocale, useLocale } from '@/i18n/locale';

import type {
  ApiCommentCreatedResponse,
  ApiCommentDeletedResponse,
  ApiCommentEditedResponse,
  ApiCommentImageResponse,
  ApiCommentImageUploadResponse,
  ApiCommentsPage,
  ApiDiscussionCount,
  ApiRepliesPage,
  ApiReportResponse,
  ApiVoteResponse,
} from '@shared/api-types';
import { communityTargetQuery, communityTargetString, mapComment, mapCommentList } from '@shared/mappers';
import type {
  CommentMention,
  CommentResult,
  CommunityComment,
  CommunityLocale,
  CommunitySort,
  CommunityTarget,
  ReportReason,
  VoteResult,
} from '@shared/types';

export const COMMENT_MAX_LENGTH = 2000;

/** Client-side ceiling for a comment attachment (~5 MB), like the app. */
export const MAX_COMMENT_IMAGE_BYTES = 5 * 1024 * 1024;

/** The image types the composer accepts (app: picker `mediaTypes: ['images']`). */
export const COMMENT_IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp';

/**
 * The list cache key for one discussion partition. An episode partition carries
 * its coordinates so a title thread and its episode threads never share cache
 * entries; the `['comments', titleType, tmdbId]` prefix still matches both.
 */
export const commentsKey = (target: CommunityTarget, sort: CommunitySort) => {
  if (target.titleType === 'episode') {
    return [
      'comments',
      target.titleType,
      target.tmdbId,
      target.seasonNumber,
      target.episodeNumber,
      sort,
    ] as const;
  }
  return ['comments', target.titleType, target.tmdbId, sort] as const;
};

export const repliesKey = (commentId: string) => ['replies', commentId] as const;

const PER_PAGE = 20;

let mutationSeq = 0;

/** Permanent client mutation id (`m<base36-ts>-<seq>`), unique process-wide. */
export function newMutationId(): string {
  return `m${Date.now().toString(36)}-${++mutationSeq}`;
}

/**
 * The discussion listing requires a community locale; web derives it from the
 * app's locale setting (getLocale — the module-level mirror <LocaleProvider>
 * keeps in sync), not from the browser. `Locale` and `CommunityLocale` share
 * the same 'en' | 'pt-BR' values, so the mapping is identity.
 */
export function discussionLocale(): CommunityLocale {
  return getLocale();
}

export function discussionTarget(target: CommunityTarget): string {
  return communityTargetString(target);
}

type CommentListPage = { comments: CommunityComment[]; cursor?: string };
type CommentListData = InfiniteData<CommentListPage>;
type Snapshot = Array<[readonly unknown[], CommentListData | undefined]>;

// --- reads --------------------------------------------------------------------

export async function fetchCommentsPage(
  target: CommunityTarget,
  sort: CommunitySort,
  cursor?: string,
): Promise<CommentListPage> {
  const query: Record<string, string | number | undefined> = {
    ...communityTargetQuery(target),
    locale: discussionLocale(),
    sort,
    per_page: PER_PAGE,
  };
  if (cursor) query.cursor = cursor;
  try {
    const r = await apiGet<ApiCommentsPage>('/v1/community/comments', { query });
    return { comments: mapCommentList(r.comments), cursor: r.cursor ?? undefined };
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return { comments: [] };
    throw err;
  }
}

export async function fetchRepliesPage(
  commentId: string,
  cursor?: string,
): Promise<CommentListPage> {
  // `sort=new` reads chronologically — the thread's reading order.
  const query: Record<string, string | number | undefined> = {
    sort: 'new',
    per_page: PER_PAGE,
  };
  if (cursor) query.cursor = cursor;
  try {
    const r = await apiGet<ApiRepliesPage>(
      `/v1/community/comments/${encodeURIComponent(commentId)}/replies`,
      { query },
    );
    return { comments: mapCommentList(r.comments), cursor: r.cursor ?? undefined };
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return { comments: [] };
    throw err;
  }
}

/** How often a `live` thread refetches while the browser tab is visible. */
export const LIVE_THREAD_REFRESH_MS = 30_000;

export interface ThreadQueryOptions {
  /**
   * Keep the thread fresh without a socket (the Discussions inbox): refetch
   * every `LIVE_THREAD_REFRESH_MS` while the tab is visible (TanStack pauses
   * interval refetches in a background tab) and on window focus. Off by
   * default — the Show / Episode pages keep their plain 60s staleness.
   */
  live?: boolean;
}

function liveOptions(live: boolean | undefined) {
  return live
    ? { refetchInterval: LIVE_THREAD_REFRESH_MS, refetchOnWindowFocus: true }
    : {};
}

/** Top-level comments for one discussion partition (title or episode),
 *  cursor-paginated. The locale rides in the query key, so switching the app
 *  language refetches the right partition. */
export function useComments(
  target: CommunityTarget,
  sort: CommunitySort,
  options: ThreadQueryOptions = {},
) {
  const { locale } = useLocale();
  return useInfiniteQuery({
    queryKey: [...commentsKey(target, sort), locale],
    queryFn: ({ pageParam }) => fetchCommentsPage(target, sort, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.cursor,
    // A sort change keeps the current list on screen until the new one
    // lands (ui-ux.md §7) instead of dropping back to a skeleton.
    placeholderData: keepPreviousData,
    ...liveOptions(options.live),
  });
}

/** Direct replies to one comment, independently paginated (chronological). */
export function useReplies(commentId: string, options: ThreadQueryOptions = {}) {
  return useInfiniteQuery({
    queryKey: repliesKey(commentId),
    queryFn: ({ pageParam }) => fetchRepliesPage(commentId, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.cursor,
    ...liveOptions(options.live),
  });
}

/**
 * `GET /v1/community/comments/count` — a cheap, cached, non-fresh estimate of a
 * title's discussion size (docs/api-schema.md §Community). The caller gates
 * `enabled` on scroll-into-view (`useInView`), and any error is swallowed to
 * `0`: a poster card never shows an error state because its count failed.
 */
export function useDiscussionCount(target: CommunityTarget | undefined, enabled: boolean) {
  return useQuery({
    queryKey: target ? ['discussionCount', communityTargetString(target)] : ['discussionCount'],
    queryFn: async () => {
      try {
        const r = await apiGet<ApiDiscussionCount>('/v1/community/comments/count', {
          query: communityTargetQuery(target!),
        });
        return Math.max(0, Math.round(r.comment_count ?? 0));
      } catch {
        return 0;
      }
    },
    enabled: enabled && !!target,
    staleTime: 30 * 60 * 1000, // 30 min — explicitly not required to be fresh
    gcTime: 60 * 60 * 1000,
  });
}

/** Flatten pages into one list, deduped — ranking may move between pages. */
export function flattenCommentPages(
  data: CommentListData | undefined,
): CommunityComment[] {
  const comments = data?.pages.flatMap((p) => p.comments) ?? [];
  const seen = new Set<string>();
  return comments.filter((c) => {
    if (seen.has(c.commentId)) return false;
    seen.add(c.commentId);
    return true;
  });
}

// --- cache plumbing -------------------------------------------------------------

/**
 * Prefix that matches exactly one discussion partition's lists (every sort):
 * a title thread's own lists, or — for an episode — only that episode's lists,
 * never a sibling episode's cached rows.
 */
function listPrefix(target: CommunityTarget): readonly unknown[] {
  if (target.titleType === 'episode') {
    return ['comments', target.titleType, target.tmdbId, target.seasonNumber, target.episodeNumber];
  }
  return ['comments', target.titleType, target.tmdbId];
}

const ALL_LIST_PREFIXES = [['comments'], ['replies']] as const;

function mapCachedLists(
  qc: QueryClient,
  prefixes: readonly (readonly unknown[])[],
  mapRow: (comment: CommunityComment) => CommunityComment | null,
) {
  for (const prefix of prefixes) {
    for (const [key, data] of qc.getQueriesData<CommentListData>({ queryKey: prefix })) {
      if (!data) continue;
      qc.setQueryData<CommentListData>(key, {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          comments: page.comments
            .map(mapRow)
            .filter((c): c is CommunityComment => c !== null),
        })),
      });
    }
  }
}

/** Patch one comment wherever it is cached (title lists and reply threads). */
function updateCachedComment(
  qc: QueryClient,
  commentId: string,
  patch: (comment: CommunityComment) => CommunityComment,
) {
  mapCachedLists(qc, ALL_LIST_PREFIXES, (c) => (c.commentId === commentId ? patch(c) : c));
}

function removeCachedComment(qc: QueryClient, commentId: string) {
  mapCachedLists(qc, ALL_LIST_PREFIXES, (c) => (c.commentId === commentId ? null : c));
}

function findCachedComment(qc: QueryClient, commentId: string): CommunityComment | undefined {
  for (const prefix of ALL_LIST_PREFIXES) {
    for (const [, data] of qc.getQueriesData<CommentListData>({ queryKey: prefix })) {
      for (const page of data?.pages ?? []) {
        const found = page.comments.find((c) => c.commentId === commentId);
        if (found) return found;
      }
    }
  }
  return undefined;
}

function prependToTitleLists(
  qc: QueryClient,
  target: CommunityTarget,
  comment: CommunityComment,
) {
  for (const [key, data] of qc.getQueriesData<CommentListData>({
    queryKey: listPrefix(target),
  })) {
    if (!data || data.pages.length === 0) continue;
    const [first, ...rest] = data.pages;
    if (first.comments.some((c) => c.commentId === comment.commentId)) continue;
    qc.setQueryData<CommentListData>(key, {
      ...data,
      pages: [{ ...first, comments: [comment, ...first.comments] }, ...rest],
    });
  }
}

function appendReplyToCache(qc: QueryClient, parentCommentId: string, reply: CommunityComment) {
  const key = repliesKey(parentCommentId);
  const data = qc.getQueryData<CommentListData>(key);
  if (!data || data.pages.length === 0) return;
  if (data.pages.some((p) => p.comments.some((c) => c.commentId === reply.commentId))) return;
  const pages = [...data.pages];
  const last = pages[pages.length - 1];
  pages[pages.length - 1] = { ...last, comments: [...last.comments, reply] };
  qc.setQueryData<CommentListData>(key, { ...data, pages });
}

function snapshotLists(qc: QueryClient, prefixes: readonly (readonly unknown[])[]): Snapshot {
  const entries: Snapshot = [];
  for (const prefix of prefixes) {
    for (const [key, data] of qc.getQueriesData<CommentListData>({ queryKey: prefix })) {
      entries.push([key, data]);
    }
  }
  return entries;
}

function restoreLists(qc: QueryClient, snapshot: Snapshot | undefined) {
  for (const [key, data] of snapshot ?? []) qc.setQueryData(key, data);
}

/**
 * Mutations with a toast + Retry on failure. The ref makes the Retry action
 * re-enter the same mutation (fresh onMutate/onError cycle) without closing
 * over a stale `mutate` (same wrapper as `show/api.ts`).
 */
function useToastedMutation<TData, TVariables, TOnMutateResult>(
  options: UseMutationOptions<TData, Error, TVariables, TOnMutateResult>,
  errorMessage: (vars: TVariables) => string,
) {
  const mutateRef = useRef<(vars: TVariables) => void>(() => {});
  const { onError, ...rest } = options;
  const mutation = useMutation<TData, Error, TVariables, TOnMutateResult>({
    ...rest,
    onError: (error, vars, onMutateResult, context) => {
      onError?.(error, vars, onMutateResult, context);
      toast.error(errorMessage(vars), {
        action: { label: translate('common.retry'), onClick: () => mutateRef.current(vars) },
      });
    },
  });
  mutateRef.current = mutation.mutate;
  return mutation;
}

// --- mutations ------------------------------------------------------------------

export interface PostCommentVars {
  mutationId: string;
  body: string;
  /** Autocompleted @mentions still present in the body (stable ids). */
  mentions?: CommentMention[];
}

/** Wire shape of `mentions` (docs/api-schema.md): only when non-empty. */
function mentionsPayload(mentions: CommentMention[] | undefined) {
  return mentions && mentions.length > 0
    ? { mentions: mentions.map((m) => ({ public_user_id: m.publicUserId, username: m.username })) }
    : {};
}

/** `POST /v1/community/comments` — prepends the optimistic row to every cached sort. */
export function usePostComment(target: CommunityTarget) {
  const qc = useQueryClient();
  return useToastedMutation<CommentResult, PostCommentVars, { previous: Snapshot }>(
    {
      mutationFn: async (vars: PostCommentVars) => {
        const r = await apiPost<ApiCommentCreatedResponse>('/v1/community/comments', {
          mutation_id: vars.mutationId,
          ...communityTargetQuery(target),
          locale: discussionLocale(),
          body: vars.body,
          ...mentionsPayload(vars.mentions),
        });
        return { comment: mapComment(r.comment), applied: r.applied };
      },
      onMutate: async (vars) => {
        await qc.cancelQueries({ queryKey: listPrefix(target) });
        const previous = snapshotLists(qc, [listPrefix(target)]);
        const optimistic: CommunityComment = {
          commentId: vars.mutationId,
          locale: discussionLocale(),
          target: discussionTarget(target),
          parentCommentId: null,
          rootCommentId: null,
          depth: 0,
          score: 0,
          replyCount: 0,
          createdAt: new Date().toISOString(),
          editedAt: null,
          moderationStatus: 'visible',
          viewerVoted: false,
          body: vars.body,
          // The row resolves the viewer's own profile card for a null author.
          author: null,
          isJustPosted: true,
        };
        prependToTitleLists(qc, target, optimistic);
        return { previous };
      },
      onError: (_err, _vars, ctx) => {
        restoreLists(qc, ctx?.previous);
      },
      onSuccess: (result) => {
        // Merge the ack over the optimistic row (comment_id === mutation_id).
        // No invalidate: under `top` sort a refetch could drop a score-0
        // comment from page one; the merged ack is the truth for this viewer
        // (the app pins just-posted rows the same way).
        updateCachedComment(qc, result.comment.commentId, () => ({
          ...result.comment,
          isJustPosted: false,
        }));
      },
    },
    () => translate('discussion.error.post'),
  );
}

export interface PostReplyVars {
  mutationId: string;
  parentCommentId: string;
  body: string;
  mentions?: CommentMention[];
}

/** `POST /v1/community/comments/:id/replies` — appends + bumps the parent's count. */
export function usePostReply(target: CommunityTarget) {
  const qc = useQueryClient();
  return useToastedMutation<CommentResult, PostReplyVars, { previous: Snapshot }>(
    {
      mutationFn: async (vars: PostReplyVars) => {
        const r = await apiPost<ApiCommentCreatedResponse>(
          `/v1/community/comments/${encodeURIComponent(vars.parentCommentId)}/replies`,
          { mutation_id: vars.mutationId, body: vars.body, ...mentionsPayload(vars.mentions) },
        );
        return { comment: mapComment(r.comment), applied: r.applied };
      },
      onMutate: async (vars) => {
        await Promise.all([
          qc.cancelQueries({ queryKey: listPrefix(target) }),
          qc.cancelQueries({ queryKey: repliesKey(vars.parentCommentId) }),
        ]);
        const previous = snapshotLists(qc, [
          listPrefix(target),
          repliesKey(vars.parentCommentId),
        ]);
        const parent = findCachedComment(qc, vars.parentCommentId);
        const optimistic: CommunityComment = {
          commentId: vars.mutationId,
          locale: discussionLocale(),
          target: parent?.target ?? discussionTarget(target),
          parentCommentId: vars.parentCommentId,
          rootCommentId: parent?.rootCommentId ?? vars.parentCommentId,
          depth: (parent?.depth ?? 0) + 1,
          score: 0,
          replyCount: 0,
          createdAt: new Date().toISOString(),
          editedAt: null,
          moderationStatus: 'visible',
          viewerVoted: false,
          body: vars.body,
          author: null,
          isJustPosted: true,
        };
        appendReplyToCache(qc, vars.parentCommentId, optimistic);
        updateCachedComment(qc, vars.parentCommentId, (c) => ({
          ...c,
          replyCount: c.replyCount + 1,
        }));
        return { previous };
      },
      onError: (_err, _vars, ctx) => {
        restoreLists(qc, ctx?.previous);
      },
      onSuccess: (result) => {
        updateCachedComment(qc, result.comment.commentId, () => ({
          ...result.comment,
          isJustPosted: false,
        }));
      },
    },
    () => translate('discussion.error.postReply'),
  );
}

/**
 * Attach one image to an existing comment — the create must be acked first
 * (the app's flow: composer posts, then the upload runs against the returned
 * `commentId`). Three legs: mint the presigned PUT (`POST .../image/upload`),
 * upload the bytes straight to S3, then confirm the key (`PUT .../image`).
 * On success the comment is patched in every cached list so the image renders
 * without a refetch; on failure the comment (text) stays and a toast offers
 * Retry.
 */
export interface AttachCommentImageVars {
  commentId: string;
  file: File;
}

/** Read a picked file's bytes — FileReader works everywhere `File` exists. */
function readFileBytes(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the image'));
    reader.readAsArrayBuffer(file);
  });
}

export function useAttachCommentImage() {
  const qc = useQueryClient();
  return useToastedMutation<string, AttachCommentImageVars, unknown>(
    {
      mutationFn: async (vars: AttachCommentImageVars) => {
        const upload = await apiPost<ApiCommentImageUploadResponse>(
          `/v1/community/comments/${encodeURIComponent(vars.commentId)}/image/upload`,
          { content_type: vars.file.type, content_length: vars.file.size },
        );
        const putRes = await fetch(upload.upload_url, {
          method: 'PUT',
          body: await readFileBytes(vars.file),
          headers: { 'Content-Type': vars.file.type },
        });
        if (!putRes.ok) throw new Error(`upload failed: ${putRes.status}`);
        const confirm = await apiPut<ApiCommentImageResponse>(
          `/v1/community/comments/${encodeURIComponent(vars.commentId)}/image`,
          { key: upload.key },
        );
        return confirm.image_key;
      },
      onSuccess: (key, vars) => {
        updateCachedComment(qc, vars.commentId, (c) => ({
          ...c,
          mediaIds: c.mediaIds?.includes(key) ? c.mediaIds : [...(c.mediaIds ?? []), key],
        }));
      },
    },
    () => translate('discussion.error.attachImage'),
  );
}

export interface VoteVars {
  commentId: string;
  voted: boolean;
}

/** `PUT`/`DELETE /v1/community/comments/:id/vote` — idempotent desired state. */
export function useCommentVote(target: CommunityTarget) {
  const qc = useQueryClient();
  return useToastedMutation<VoteResult, VoteVars, { previous: Snapshot; parentId: string | null }>(
    {
      mutationFn: async (vars: VoteVars) => {
        const path = `/v1/community/comments/${encodeURIComponent(vars.commentId)}/vote`;
        const r = vars.voted
          ? await apiPut<ApiVoteResponse>(path)
          : await apiDelete<ApiVoteResponse>(path);
        return {
          voted: r.voted,
          applied: r.applied,
          score: Math.max(0, Math.round(r.score ?? 0)),
        };
      },
      onMutate: async (vars) => {
        await Promise.all([
          qc.cancelQueries({ queryKey: listPrefix(target) }),
          qc.cancelQueries({ queryKey: ['replies'] }),
        ]);
        const parentId = findCachedComment(qc, vars.commentId)?.parentCommentId ?? null;
        const previous = snapshotLists(qc, [listPrefix(target), ['replies']]);
        updateCachedComment(qc, vars.commentId, (c) => ({
          ...c,
          score: Math.max(0, c.score + (vars.voted ? 1 : -1)),
          viewerVoted: vars.voted,
        }));
        return { previous, parentId };
      },
      onError: (_err, _vars, ctx) => {
        restoreLists(qc, ctx?.previous);
      },
      onSuccess: (result, vars) => {
        // The response carries the authoritative score.
        updateCachedComment(qc, vars.commentId, (c) => ({
          ...c,
          score: result.score,
          viewerVoted: result.voted,
        }));
      },
      onSettled: (_data, _err, _vars, ctx) => {
        void qc.invalidateQueries({ queryKey: listPrefix(target) });
        if (ctx?.parentId) void qc.invalidateQueries({ queryKey: repliesKey(ctx.parentId) });
      },
    },
    () => translate('discussion.error.vote'),
  );
}

export interface EditCommentVars {
  commentId: string;
  body: string;
}

/** `PUT /v1/community/comments/:id` — the author's own visible comment. */
export function useEditComment(target: CommunityTarget) {
  const qc = useQueryClient();
  return useToastedMutation<CommentResult, EditCommentVars, { previous: Snapshot; parentId: string | null }>(
    {
      mutationFn: async (vars: EditCommentVars) => {
        const r = await apiPut<ApiCommentEditedResponse>(
          `/v1/community/comments/${encodeURIComponent(vars.commentId)}`,
          { body: vars.body },
        );
        return { comment: mapComment(r.comment), applied: r.applied };
      },
      onMutate: async (vars) => {
        await Promise.all([
          qc.cancelQueries({ queryKey: listPrefix(target) }),
          qc.cancelQueries({ queryKey: ['replies'] }),
        ]);
        const parentId = findCachedComment(qc, vars.commentId)?.parentCommentId ?? null;
        const previous = snapshotLists(qc, [listPrefix(target), ['replies']]);
        updateCachedComment(qc, vars.commentId, (c) => ({
          ...c,
          body: vars.body,
          editedAt: new Date().toISOString(),
        }));
        return { previous, parentId };
      },
      onError: (_err, _vars, ctx) => {
        restoreLists(qc, ctx?.previous);
      },
      onSuccess: (result) => {
        updateCachedComment(qc, result.comment.commentId, () => result.comment);
      },
      onSettled: (_data, _err, _vars, ctx) => {
        void qc.invalidateQueries({ queryKey: listPrefix(target) });
        if (ctx?.parentId) void qc.invalidateQueries({ queryKey: repliesKey(ctx.parentId) });
      },
    },
    () => translate('discussion.error.edit'),
  );
}

export interface DeleteCommentVars {
  commentId: string;
}

/**
 * `DELETE /v1/community/comments/:id` — a comment with replies tombstones
 * (anonymous structural node); without replies it disappears entirely.
 */
export function useDeleteComment(target: CommunityTarget) {
  const qc = useQueryClient();
  return useToastedMutation<
    { deleted: boolean; tombstoned: boolean },
    DeleteCommentVars,
    { previous: Snapshot; parentId: string | null }
  >(
    {
      mutationFn: async (vars: DeleteCommentVars) => {
        const r = await apiDelete<ApiCommentDeletedResponse>(
          `/v1/community/comments/${encodeURIComponent(vars.commentId)}`,
        );
        return { deleted: r.deleted, tombstoned: r.tombstoned };
      },
      onMutate: async (vars) => {
        await Promise.all([
          qc.cancelQueries({ queryKey: listPrefix(target) }),
          qc.cancelQueries({ queryKey: ['replies'] }),
        ]);
        const cached = findCachedComment(qc, vars.commentId);
        const parentId = cached?.parentCommentId ?? null;
        const previous = snapshotLists(qc, [listPrefix(target), ['replies']]);
        if (cached && cached.replyCount > 0) {
          updateCachedComment(qc, vars.commentId, (c) => ({
            ...c,
            moderationStatus: 'deleted',
            body: null,
            author: null,
          }));
        } else {
          removeCachedComment(qc, vars.commentId);
          qc.removeQueries({ queryKey: repliesKey(vars.commentId) });
        }
        return { previous, parentId };
      },
      onError: (_err, _vars, ctx) => {
        restoreLists(qc, ctx?.previous);
      },
      onSuccess: (result, vars) => {
        // Reconcile with the server truth (the tombstone guess may differ).
        if (result.tombstoned) {
          updateCachedComment(qc, vars.commentId, (c) => ({
            ...c,
            moderationStatus: 'deleted',
            body: null,
            author: null,
          }));
        } else {
          removeCachedComment(qc, vars.commentId);
        }
      },
      onSettled: (_data, _err, _vars, ctx) => {
        void qc.invalidateQueries({ queryKey: listPrefix(target) });
        if (ctx?.parentId) void qc.invalidateQueries({ queryKey: repliesKey(ctx.parentId) });
      },
    },
    () => "Couldn't delete the comment",
  );
}

export interface ReportCommentVars {
  commentId: string;
  reason: ReportReason;
}

/** `POST /v1/community/comments/:id/report` — once per user; never optimistic. */
export function useReportComment() {
  return useToastedMutation<boolean, ReportCommentVars, unknown>(
    {
      mutationFn: async (vars: ReportCommentVars) => {
        const r = await apiPost<ApiReportResponse>(
          `/v1/community/comments/${encodeURIComponent(vars.commentId)}/report`,
          { reason: vars.reason },
        );
        return r.reported;
      },
      onSuccess: () => {
        toast.success(translate('discussion.reportSubmitted'));
      },
    },
    () => translate('discussion.error.report'),
  );
}
